import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../lib/api';

interface SearchResult {
  kind: 'case' | 'franchisee' | 'run';
  id: string;
  label: string;
  sub?: string;
}

interface HeaderProps {
  user: { name: string; initials: string; role: string };
  onLogout?: () => void | Promise<void>;
  onOpenCase?: (caseId: string) => void;
  onNavRuns?: () => void;
}

export function Header({ user, onLogout, onOpenCase, onNavRuns }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [showResults, setShowResults] = useState(false);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults(null); return; }
    debounce.current = window.setTimeout(() => {
      api<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(q)}`)
        .then((d) => setResults(d.results))
        .catch(() => setResults([]));
    }, 200);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [q]);

  const pickResult = (r: SearchResult) => {
    setShowResults(false);
    setQ('');
    if (r.kind === 'case' && onOpenCase) onOpenCase(r.id);
    else if (r.kind === 'run' && onNavRuns) onNavRuns();
    // franchisee: no nav target yet
  };
  return (
    <header style={{
      height: 52,
      background: 'var(--header-bg)',
      color: 'var(--header-fg)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      gap: 16,
      flexShrink: 0,
      zIndex: 40,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 26, height: 26, borderRadius: 6,
          background: 'linear-gradient(135deg, oklch(0.55 0.10 200), oklch(0.40 0.08 200))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color: '#fff',
          fontFamily: 'var(--font-mono)',
        }}>AFI</div>
        <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }}>Encroachment Audit Console</div>
        <div style={{ fontSize: 10.5, padding: '2px 6px', borderRadius: 3, background: 'rgba(255,255,255,0.10)', fontWeight: 500, letterSpacing: '0.04em', color: 'var(--header-fg-faint)' }}>SPEC v0.6.0</div>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: 380, maxWidth: '100%' }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--header-fg-faint)' }}>
            <Icon name="search" size={14} />
          </div>
          <input
            placeholder="Search cases, clients, AF#, run IDs…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            style={{
              width: '100%', height: 32,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.10)',
              color: 'var(--header-fg)',
              borderRadius: 6,
              padding: '0 80px 0 32px',
              outline: 'none',
              fontSize: 13,
            }}
          />
          {showResults && results && results.length > 0 && (
            <>
              <div onClick={() => setShowResults(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 8,
                boxShadow: 'var(--shadow-lg)', maxHeight: 360, overflow: 'auto',
              }}>
                {results.map((r) => (
                  <button
                    key={`${r.kind}-${r.id}`}
                    onClick={() => pickResult(r)}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 1,
                      padding: '8px 12px', width: '100%', textAlign: 'left',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)', color: 'inherit',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                      <span className="mono" style={{ fontSize: 10, padding: '1px 5px', background: 'var(--surface-2)', borderRadius: 3, color: 'var(--text-muted)' }}>{r.kind}</span>
                      <span>{r.label}</span>
                    </div>
                    {r.sub && <div className="muted" style={{ fontSize: 11.5, marginLeft: 38 }}>{r.sub}</div>}
                  </button>
                ))}
              </div>
            </>
          )}
          {showResults && results && results.length === 0 && q.trim().length >= 2 && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
              background: 'var(--surface)', color: 'var(--text-muted)',
              border: '1px solid var(--border)', borderRadius: 8,
              padding: '10px 14px', fontSize: 12.5,
            }}>
              No matches for &quot;{q}&quot;.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
              background: menuOpen ? 'rgba(255,255,255,0.08)' : 'transparent',
              border: '1px solid transparent', borderRadius: 6, cursor: 'default',
              color: 'inherit',
            }}
          >
            <div className="avatar" style={{ width: 28, height: 28, background: 'oklch(0.45 0.07 200)', color: '#fff', fontSize: 11.5 }}>{user.initials}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, lineHeight: 1.15, textAlign: 'left' }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>{user.name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--header-fg-faint)' }}>{user.role}</div>
            </div>
            <Icon name="chevdown" size={11} color="var(--header-fg-faint)" />
          </button>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 8,
                boxShadow: 'var(--shadow-lg)', minWidth: 200, padding: 6,
              }}>
                <button
                  className="btn btn-ghost"
                  style={{ width: '100%', justifyContent: 'flex-start', height: 32 }}
                  onClick={async () => { setMenuOpen(false); if (onLogout) await onLogout(); window.location.href = '/'; }}
                >
                  <Icon name="logout" size={14} /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
